import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge"
import { format, parseISO, isValid } from "date-fns";
import { ptBR } from "date-fns/locale";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Nome curto pra saudações (ex.: "Boa noite, {nome}" no dashboard) — usa o
 * nome de exibição escolhido pela pessoa ou, se não tiver, o primeiro nome
 * do nome completo (evita nomes completos gigantes quebrando o layout).
 */
export function getGreetingName(user?: { name?: string; display_name?: string | null } | null): string {
  if (!user) return '';
  const trimmedDisplayName = user.display_name?.trim();
  if (trimmedDisplayName) return trimmedDisplayName;
  return user.name?.trim().split(/\s+/)[0] || '';
}

/**
 * Formata uma data (string ou Date) para o padrão dd/MM/yy.
 * Ex: 2024-12-31 -> 31/12/24
 */
function parseFlexibleDate(date: string): Date {
  if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return parseISO(`${date}T12:00:00`);
  }
  return parseISO(date);
}

export function formatDate(date: string | Date | null | undefined): string {
  if (!date) return "—";
  try {
    const d = typeof date === "string" ? parseFlexibleDate(date) : date;
    if (!isValid(d)) return "Data inválida";
    return format(d, "dd/MM/yy");
  } catch (err) {
    return "—";
  }
}

/**
 * Data por extenso em português.
 * Ex: 2026-05-28 -> 28 de maio de 2026
 */
export function formatDateLong(date: string | Date | null | undefined): string {
  if (!date) return "—";
  try {
    const d = typeof date === "string" ? parseFlexibleDate(date) : date;
    if (!isValid(d)) return "Data inválida";
    return format(d, "dd 'de' MMMM 'de' yyyy", { locale: ptBR });
  } catch {
    return "—";
  }
}

/**
 * Data e hora para exibição em logs.
 * Ex: 2026-05-28T20:41:00 -> 28/05/2026 às 20:41
 */
export function formatDateTime(date: string | Date | null | undefined): string {
  if (!date) return "—";
  try {
    const d = typeof date === "string" ? parseISO(date) : date;
    if (!isValid(d)) return "Data inválida";
    return format(d, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
  } catch {
    return "—";
  }
}

/**
 * Formata um mês de referência (YYYY-MM) para o padrão MM/yyyy.
 * Ex: 2024-12 -> 12/2024
 */
export function formatMonthYear(monthStr: string | null | undefined): string {
  if (!monthStr) return "—";
  try {
    // Se for YYYY-MM, formatamos como MM/yyyy
    const parts = monthStr.split("-");
    if (parts.length === 2) {
      return `${parts[1]}/${parts[0]}`;
    }
    // Caso seja uma data completa YYYY-MM-DD
    const d = parseISO(monthStr);
    if (!isValid(d)) return monthStr;
    return format(d, "MM/yyyy");
  } catch (err) {
    return monthStr || "—";
  }
}

/**
 * Traduz o método de pagamento para linguagem natural
 */
export function translatePaymentMethod(method?: string | null): string {
  if (!method) return "—";
  const m = method.toLowerCase().trim();
  switch (m) {
    case 'credit_card':
    case 'cartao_credito':
    case 'cartão de crédito':
      return 'Cartão de Crédito';
    case 'debit_card':
    case 'debito':
    case 'débito':
    case 'cartao_debito':
    case 'cartão de débito':
      return 'Cartão de Débito';
    case 'cash':
    case 'dinheiro':
      return 'Dinheiro';
    case 'pix':
      return 'PIX';
    case 'bank_transfer':
    case 'transferencia':
    case 'transferência':
      return 'Transferência Bancária';
    case 'health_insurance':
    case 'convenio':
    case 'convênio':
      return 'Convênio';
    case 'a_definir':
      return 'A definir';
    default:
      return method;
  }
}

/**
 * Traduz valores comuns de status e campos do banco para PT-BR
 */
export function translateValue(value: any): string {
  if (value === 'paid') return 'Pago';
  if (value === 'pending') return 'Pendente';
  if (value === 'cancelled') return 'Cancelado';
  if (value === 'scheduled') return 'Agendado';
  if (value === 'completed') return 'Finalizado';
  if (['credit_card', 'cartao_credito', 'cartão de crédito'].includes(value)) return 'Cartão de Crédito';
  if (['debit_card', 'debito', 'débito', 'cartao_debito', 'cartão de débito'].includes(value)) return 'Cartão de Débito';
  if (['cash', 'dinheiro'].includes(value)) return 'Dinheiro';
  if (value === 'pix') return 'PIX';
  if (['bank_transfer', 'transferencia', 'transferência'].includes(value)) return 'Transferência Bancária';
  if (['health_insurance', 'convenio', 'convênio'].includes(value)) return 'Convênio';
  if (value === 'no_show') return 'Não compareceu';
  if (value === 'excused_absence') return 'Falta justificada';
  if (value === 'single') return 'Consulta única';
  if (value === 'session') return 'Sessão recorrente';
  if (value === 'package') return 'Pacote';
  if (value === 'admin') return 'Administrador';
  if (value === 'secretary') return 'Secretária';
  if (value === 'professional') return 'Profissional';
  return String(value);
}

const AUDIT_DATE_KEYS = new Set([
  'date', 'data', 'due_date', 'payment_date', 'birth_date', 'deleted_at',
]);
const AUDIT_MONTH_KEYS = new Set(['month', 'month_reference']);
const AUDIT_TIME_KEYS = new Set(['time', 'hora', 'end_time', 'time_start', 'time_end']);
const AUDIT_BOOLEAN_KEYS = new Set([
  'bulk', 'is_active', 'is_installment', 'confirmed', 'whatsapp_automated', 'waiting_service',
]);
const AUDIT_CURRENCY_KEYS = new Set(['amount', 'total', 'amount_paid', 'commission_rate']);

/**
 * Formata valores de metadata_info nos logs de auditoria.
 */
export function formatAuditMetadataValue(key: string, value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';

  if (AUDIT_BOOLEAN_KEYS.has(key) || typeof value === 'boolean') {
    return value ? 'Sim' : 'Não';
  }

  if (typeof value === 'number' && AUDIT_CURRENCY_KEYS.has(key)) {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  }

  if (typeof value === 'string') {
    if (AUDIT_DATE_KEYS.has(key)) return formatDate(value);
    if (AUDIT_MONTH_KEYS.has(key)) return formatMonthYear(value);
    if (AUDIT_TIME_KEYS.has(key)) return value;
    if (key === 'status' || key === 'new_status' || key === 'payment_status' || key === 'role' || key === 'type' || key === 'payment_method') {
      return translateValue(value);
    }
  }

  if (Array.isArray(value)) {
    return value.map((item) => formatAuditMetadataValue(key, item)).join(', ');
  }

  if (typeof value === 'object') {
    return Object.entries(value as Record<string, unknown>)
      .map(([nestedKey, nestedValue]) => {
        const label = translateAuditMetadataKey(nestedKey);
        return `${label}: ${formatAuditMetadataValue(nestedKey, nestedValue)}`;
      })
      .join(' · ');
  }

  return translateValue(value);
}

/** Rótulos em português para chaves comuns de metadata de auditoria. */
export function translateAuditMetadataKey(key: string): string {
  const keys: Record<string, string> = {
    amount: 'Valor',
    amount_paid: 'Valor pago',
    status: 'Situação',
    new_status: 'Novo status',
    payment_status: 'Status do pagamento',
    date: 'Data',
    data: 'Data',
    time: 'Horário',
    hora: 'Horário',
    end_time: 'Término',
    time_start: 'Início',
    time_end: 'Término',
    notes: 'Observações',
    observation: 'Observação',
    patient_name: 'Paciente',
    username: 'Usuário',
    name: 'Nome',
    phone: 'Telefone',
    role: 'Cargo',
    is_active: 'Ativo',
    description: 'Descrição',
    category: 'Categoria',
    due_date: 'Vencimento',
    payment_date: 'Data de pagamento',
    patient_id: 'ID paciente',
    subscription_id: 'ID assinatura',
    appointment_id: 'ID agendamento',
    service_id: 'ID serviço',
    installment_id: 'ID parcela',
    installment_number: 'Parcela nº',
    installment_count: 'Qtd. parcelas',
    month: 'Mês ref.',
    month_reference: 'Mês ref.',
    reason: 'Motivo',
    batch_number: 'Nº do lote',
    insurance_name: 'Convênio',
    guide_count: 'Qtd. guias',
    content: 'Conteúdo',
    payment_method: 'Método de pagamento',
    bulk: 'Em lote',
    count: 'Quantidade',
    type: 'Tipo',
    is_installment: 'Parcelado',
    confirmed: 'Confirmado',
    waiting_service: 'Aguardando atendimento',
    whatsapp_automated: 'WhatsApp automático',
    email: 'E-mail',
    cpf: 'CPF',
    address: 'Endereço',
  };
  return keys[key] || key.replace(/_/g, ' ');
}
